import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  ClipboardList, Calendar, CheckCircle2, Clock, AlertCircle, 
  ChevronRight, Phone, User, MapPin, Package, Truck, Users,
  Plus, Trash2, CalendarX, Copy, MessageSquare
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate WhatsApp-friendly text for logistics request
const generateWhatsAppText = (request) => {
  if (!request) return "";
  
  const game = request.game || {};
  const team = request.team || {};
  
  // Format date
  const gameDate = game.date ? new Date(game.date) : new Date();
  const dateStr = gameDate.toLocaleDateString("en-SG", { 
    weekday: "short", 
    day: "numeric", 
    month: "short" 
  });
  const timeStr = gameDate.toLocaleTimeString("en-SG", { 
    hour: "2-digit", 
    minute: "2-digit", 
    hour12: false 
  }) + "hrs";
  
  let text = `🏆 *LOGISTICS REQUEST*\n\n`;
  text += `*${team.name || "Team"} vs ${game.opponent || "Opponent"}*\n`;
  text += `📅 ${dateStr}\n`;
  text += `⏰ Kick off @${timeStr}\n`;
  text += `📍 ${game.location || "TBD"}\n`;
  
  // Referee
  if (request.request_referee) {
    text += `\n⚖️ *Referee Request:* ${request.referee_count || 3} officials\n`;
  }
  
  // Transport
  if (request.request_transport) {
    text += `\n🚐 *Transport Required*\n`;
    if (request.transport_notes) {
      text += `${request.transport_notes}\n`;
    }
  }
  
  // Equipment
  if (request.equipment_items && request.equipment_items.length > 0) {
    const readyTime = request.equipment_ready_time ? ` (ready by ${request.equipment_ready_time})` : "";
    text += `\n🎒 *Equipment needed*${readyTime}\n`;
    request.equipment_items.forEach((item, idx) => {
      let itemText = `${idx + 1}. ${item.name}`;
      if (item.quantity > 1) itemText += ` x${item.quantity}`;
      if (item.color) itemText += ` (${item.color})`;
      if (item.notes) itemText += ` - ${item.notes}`;
      text += `${itemText}\n`;
    });
  }
  
  // Refreshments
  if (request.request_refreshments && request.refreshments_details) {
    text += `\n🥤 *Refreshments:*\n${request.refreshments_details}\n`;
  }
  
  // Special instructions
  if (request.special_instructions) {
    text += `\n📝 *Special Instructions:*\n${request.special_instructions}\n`;
  }
  
  // Contact
  text += `\n👤 *Contact:* ${request.contact_name || "N/A"}`;
  if (request.contact_phone) {
    text += `\n📞 ${request.contact_phone}`;
  }
  
  text += `\n\nThanks! 🙏`;
  
  return text;
};

export default function StaffPortal({ user }) {
  const [requests, setRequests] = useState([]);
  const [blockouts, setBlockouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showBlockoutDialog, setShowBlockoutDialog] = useState(false);
  const [newBlockout, setNewBlockout] = useState({
    venue_name: "",
    start_date: "",
    end_date: "",
    reason: ""
  });

  useEffect(() => {
    fetchRequests();
    fetchBlockouts();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/staff/logistics${params}`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching logistics requests:", error);
      toast.error("Failed to load logistics requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockouts = async () => {
    try {
      const response = await axios.get(`${API}/staff/field-blockouts`, { withCredentials: true });
      setBlockouts(response.data);
    } catch (error) {
      console.error("Error fetching blockouts:", error);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/staff/logistics/${requestId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Request marked as ${newStatus}`);
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  const addStaffNotes = async (requestId, notes) => {
    try {
      await axios.put(`${API}/staff/logistics/${requestId}`, { staff_notes: notes }, { withCredentials: true });
      toast.success("Notes saved");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const copyToWhatsApp = (request) => {
    const text = generateWhatsAppText(request);
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard! Paste in WhatsApp");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const createBlockout = async () => {
    try {
      await axios.post(`${API}/staff/field-blockouts`, newBlockout, { withCredentials: true });
      toast.success("Field blockout created");
      setShowBlockoutDialog(false);
      setNewBlockout({ venue_name: "", start_date: "", end_date: "", reason: "" });
      fetchBlockouts();
    } catch (error) {
      toast.error("Failed to create blockout");
    }
  };

  const deleteBlockout = async (blockoutId) => {
    if (!window.confirm("Delete this field blockout?")) return;
    try {
      await axios.delete(`${API}/staff/field-blockouts/${blockoutId}`, { withCredentials: true });
      toast.success("Blockout deleted");
      fetchBlockouts();
    } catch (error) {
      toast.error("Failed to delete blockout");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      confirmed: "bg-blue-500/20 text-blue-400 border-blue-500/50",
      in_progress: "bg-purple-500/20 text-purple-400 border-purple-500/50",
      completed: "bg-green-500/20 text-green-400 border-green-500/50"
    };
    const labels = {
      pending: "Pending",
      confirmed: "Confirmed",
      in_progress: "In Progress",
      completed: "Completed"
    };
    return (
      <Badge className={`${styles[status] || styles.pending} border`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-SG", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: false }) + "hrs";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Staff Portal</h1>
          <p className="text-slate-400">Manage logistics requests and field availability</p>
        </div>

        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="requests" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <ClipboardList className="w-4 h-4 mr-2" />
              Logistics Requests
            </TabsTrigger>
            <TabsTrigger value="calendar" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Calendar className="w-4 h-4 mr-2" />
              Field Calendar
            </TabsTrigger>
          </TabsList>

          {/* Logistics Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="all">All Requests</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="text-slate-400 text-sm">
                {requests.length} request{requests.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Request Cards */}
            {requests.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700 p-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No logistics requests found</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {requests.map((request) => (
                  <Card 
                    key={request.id} 
                    className="bg-slate-800/50 border-slate-700 p-4 hover:border-yellow-500/50 transition-all cursor-pointer"
                    onClick={() => setSelectedRequest(request)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-bold text-white">
                            {request.team?.name || "Team"} vs {request.game?.opponent || "Opponent"}
                          </h3>
                          {getStatusBadge(request.status)}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(request.game?.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(request.game?.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {request.game?.location || "TBD"}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-2">
                          {request.request_referee && (
                            <Badge variant="outline" className="border-blue-500/50 text-blue-400">
                              <Users className="w-3 h-3 mr-1" />
                              {request.referee_count} Officials
                            </Badge>
                          )}
                          {request.request_transport && (
                            <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                              <Truck className="w-3 h-3 mr-1" />
                              Transport
                            </Badge>
                          )}
                          {request.equipment_items?.length > 0 && (
                            <Badge variant="outline" className="border-green-500/50 text-green-400">
                              <Package className="w-3 h-3 mr-1" />
                              {request.equipment_items.length} Items
                            </Badge>
                          )}
                          {request.request_refreshments && (
                            <Badge variant="outline" className="border-orange-500/50 text-orange-400">
                              Refreshments
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Field Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Field Blockouts</h2>
              <Dialog open={showBlockoutDialog} onOpenChange={setShowBlockoutDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Blockout
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Add Field Blockout</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm text-slate-400">Venue</label>
                      <Input
                        value={newBlockout.venue_name}
                        onChange={(e) => setNewBlockout({...newBlockout, venue_name: e.target.value})}
                        placeholder="e.g., Dempsey, Padang"
                        className="bg-slate-900 border-slate-600"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-slate-400">Start Date</label>
                        <Input
                          type="date"
                          value={newBlockout.start_date}
                          onChange={(e) => setNewBlockout({...newBlockout, start_date: e.target.value})}
                          className="bg-slate-900 border-slate-600"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-400">End Date</label>
                        <Input
                          type="date"
                          value={newBlockout.end_date}
                          onChange={(e) => setNewBlockout({...newBlockout, end_date: e.target.value})}
                          className="bg-slate-900 border-slate-600"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-400">Reason</label>
                      <Input
                        value={newBlockout.reason}
                        onChange={(e) => setNewBlockout({...newBlockout, reason: e.target.value})}
                        placeholder="e.g., Field maintenance, SCC event"
                        className="bg-slate-900 border-slate-600"
                      />
                    </div>
                    <Button onClick={createBlockout} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black">
                      Create Blockout
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {blockouts.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700 p-12 text-center">
                <CalendarX className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No field blockouts scheduled</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {blockouts.map((blockout) => (
                  <Card key={blockout.id} className="bg-slate-800/50 border-slate-700 p-4">
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <h3 className="font-bold text-white">{blockout.venue_name || "All Venues"}</h3>
                        <p className="text-slate-400 text-sm">
                          {formatDate(blockout.start_date)} - {formatDate(blockout.end_date)}
                        </p>
                        {blockout.reason && (
                          <p className="text-slate-500 text-sm">{blockout.reason}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteBlockout(blockout.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Request Details Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedRequest && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    {selectedRequest.team?.name} vs {selectedRequest.game?.opponent}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Game Info */}
                  <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedRequest.game?.date)} at {formatTime(selectedRequest.game?.date)}
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <MapPin className="w-4 h-4" />
                      {selectedRequest.game?.location}
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Contact Person</h4>
                    <div className="flex items-center gap-2 text-slate-300">
                      <User className="w-4 h-4" />
                      {selectedRequest.contact_name}
                    </div>
                    {selectedRequest.contact_phone && (
                      <div className="flex items-center gap-2 text-slate-300 mt-1">
                        <Phone className="w-4 h-4" />
                        {selectedRequest.contact_phone}
                      </div>
                    )}
                  </div>

                  {/* Referee Request */}
                  {selectedRequest.request_referee && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-400 mb-2">Referee Request</h4>
                      <p className="text-slate-300">{selectedRequest.referee_count} officials required</p>
                    </div>
                  )}

                  {/* Transport Request */}
                  {selectedRequest.request_transport && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-400 mb-2">Transport Request</h4>
                      <p className="text-slate-300">{selectedRequest.transport_notes || "Equipment transport required"}</p>
                    </div>
                  )}

                  {/* Equipment List */}
                  {selectedRequest.equipment_items?.length > 0 && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h4 className="font-semibold text-green-400 mb-2">
                        Equipment Needed
                        {selectedRequest.equipment_ready_time && (
                          <span className="font-normal text-sm ml-2">
                            (ready by {selectedRequest.equipment_ready_time})
                          </span>
                        )}
                      </h4>
                      <ul className="space-y-1">
                        {selectedRequest.equipment_items.map((item, idx) => (
                          <li key={idx} className="text-slate-300">
                            {idx + 1}. {item.name}
                            {item.quantity > 1 && ` x${item.quantity}`}
                            {item.color && ` (${item.color})`}
                            {item.notes && ` - ${item.notes}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Refreshments */}
                  {selectedRequest.request_refreshments && selectedRequest.refreshments_details && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-400 mb-2">Refreshments</h4>
                      <p className="text-slate-300 whitespace-pre-line">{selectedRequest.refreshments_details}</p>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {selectedRequest.special_instructions && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Special Instructions</h4>
                      <p className="text-slate-300 whitespace-pre-line">{selectedRequest.special_instructions}</p>
                    </div>
                  )}

                  {/* Staff Notes */}
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Staff Notes</h4>
                    <Textarea
                      defaultValue={selectedRequest.staff_notes || ""}
                      placeholder="Add notes about this request..."
                      className="bg-slate-800 border-slate-600 text-white"
                      onBlur={(e) => {
                        if (e.target.value !== selectedRequest.staff_notes) {
                          addStaffNotes(selectedRequest.id, e.target.value);
                        }
                      }}
                    />
                  </div>

                  {/* Status Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => updateRequestStatus(selectedRequest.id, "confirmed")}
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={selectedRequest.status === "confirmed"}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirm
                    </Button>
                    <Button
                      onClick={() => updateRequestStatus(selectedRequest.id, "in_progress")}
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={selectedRequest.status === "in_progress"}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      In Progress
                    </Button>
                    <Button
                      onClick={() => updateRequestStatus(selectedRequest.id, "completed")}
                      className="bg-green-600 hover:bg-green-700"
                      disabled={selectedRequest.status === "completed"}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Complete
                    </Button>
                  </div>
                  
                  {/* WhatsApp Export */}
                  <div className="border-t border-slate-700 pt-4 mt-4">
                    <Button
                      onClick={() => copyToWhatsApp(selectedRequest)}
                      variant="outline"
                      className="w-full border-green-500/50 text-green-400 hover:bg-green-500/10"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Copy for WhatsApp
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Copy formatted text to paste in WhatsApp for ground staff
                    </p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
